/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import {lazy, useState, useEffect} from 'react';
import {BrowserRouter, Route, Routes, Outlet} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {IAppStore} from 'store';
import urlRoutes from 'routers/url-routes';

import SuspenseComponent from 'components/suspense-component';
import withTitle from 'components/withTitle';
import Header from 'components/header';
import {AppContainer} from 'components/layout';
import Copyright from 'components/copyright';
import {Redirect} from 'components/redirect';
import PageError from 'components/error-page';

const AppRouter = (): JSX.Element => {
  const {
    login,
    channels,
    rooms,
    analytics,
    qos,
    dashboard
  } = urlRoutes;
  const applicationId = useSelector((state: IAppStore) => state.user.applicationId);
  const [id, setId] = useState(applicationId);
  const Login = withTitle(SuspenseComponent(lazy(() => import('views/login'))), 'Login');
  const Channels = withTitle(SuspenseComponent(lazy(() => import('views/channels'))), 'Channels');
  const Channel = SuspenseComponent(lazy(() => import('views/channel-details')));
  const PreviewChannel = withTitle(SuspenseComponent(lazy(() => import('views/preview'))), 'Channel Preview');
  const ChannelStreams = withTitle(SuspenseComponent(lazy(() => import('views/channel-details/streams'))), 'Channel Streams');
  const ChannelProperties = withTitle(SuspenseComponent(lazy(() => import('views/properties'))), 'Channel Properties');
  const ChannelAnalytics = SuspenseComponent(lazy(() => import('views/channel-details/analytics')));
  const ChannelAnalyticsReports = withTitle(SuspenseComponent(lazy(() => import('views/channel-details/analytics/reports'))), 'Channel Reports');
  const ChannelPublishingHistory = withTitle(SuspenseComponent(lazy(() => import('views/channel-details/analytics/publishing-history'))), 'Channel Publishing History');
  const ChannelForkHistory = withTitle(SuspenseComponent(lazy(() => import('views/channel-details/analytics/fork-history'))), 'Channel Forking History');
  const ChannelConcurrents = withTitle(SuspenseComponent(lazy(() => import('views/channel-details/analytics/concurrents'))), 'Channel Concurrent Viewing Streams');
  const ChannelIngest = withTitle(SuspenseComponent(lazy(() => import('views/channel-details/analytics/ingest'))), 'Channel Ingest Report');
  const ChannelMessages = withTitle(SuspenseComponent(lazy(() => import('views/messages'))), 'Channel Messages');
  const ChannelEdgeAuth = SuspenseComponent(lazy(() => import('views/edge-auth')));
  const ChannelPublishingPermalinks = withTitle(SuspenseComponent(lazy(() => import('views/edge-auth/publishing'))), 'Create Publishing Token for Channel');
  const ChannelViewingPermalinks = withTitle(SuspenseComponent(lazy(() => import('views/edge-auth/viewing'))), 'Create Viewing Token for Channel');
  const PublishPullChannel = withTitle(SuspenseComponent(lazy(() => import('views/publish-pull'))), 'Publish to Channel with URI');
  const Diagnose = withTitle(SuspenseComponent(lazy(() => import('views/decode-token'))), 'Diagnose');
  const Rooms = withTitle(SuspenseComponent(lazy(() => import('views/rooms'))), 'Rooms');
  const Room = SuspenseComponent(lazy(() => import('views/room-details')));
  const RoomPreview = withTitle(SuspenseComponent(lazy(() => import('views/preview'))), 'Room Preview');
  const RoomMembers = withTitle(SuspenseComponent(lazy(() => import('views/room-details/members'))), 'Room Members');
  const RoomProperties = withTitle(SuspenseComponent(lazy(() => import('views/properties'))), 'Room Properties');
  const RoomMessages = withTitle(SuspenseComponent(lazy(() => import('views/messages'))), 'Room Messages');
  const RoomEdgeAuth = SuspenseComponent(lazy(() => import('views/edge-auth')));
  const RoomPublishingPermalinks = withTitle(SuspenseComponent(lazy(() => import('views/edge-auth/publishing'))), 'Create Publishing Token for Room');
  const RoomViewingPermalinks = withTitle(SuspenseComponent(lazy(() => import('views/edge-auth/viewing'))), 'Create Viewing Token for Room');
  const PublishPullRoom = withTitle(SuspenseComponent(lazy(() => import('views/publish-pull'))), 'Publish to Room with URI');
  const Analytics = SuspenseComponent(lazy(() => import('views/analytics')));
  const AnalyticsSummary = withTitle(SuspenseComponent(lazy(() => import('views/analytics/summary'))), 'Analytics Summary');
  const AnalyticsCurrentActivity = withTitle(SuspenseComponent(lazy(() => import('views/analytics/current-activity'))), 'Current Activity');
  const AnalyticsUsageStatistics = withTitle(SuspenseComponent(lazy(() => import('views/analytics/usage'))), 'Usage Statistics');
  const AnalyticsTimeToFirstFrame = withTitle(SuspenseComponent(lazy(() => import('views/analytics/time-to-first-frame'))), 'Time to First Frame Statistics');
  const AnalyticsReports = withTitle(SuspenseComponent(lazy(() => import('views/analytics/reports'))), 'Reports');
  const QoS = withTitle(SuspenseComponent(lazy(() => import('views/qos'))), 'Quality of Service');
  const Dashboard = withTitle(SuspenseComponent(lazy(() => import('views/dashboard'))), 'Dashboard');
  const baseLocation = document.querySelector('base') ? new URL(document.baseURI) : null;
  const basename = baseLocation?.pathname || '/';

  useEffect(() => {
    if (id !== applicationId) {
      setId(applicationId);
    }
  }, [applicationId]);

  const PageNotFound = withTitle(() => {
    const {channels} = urlRoutes;

    return (
      <PageError
        status={'404'}
        text={'Page not found'}
        button={{
          path: `/${channels.path}`,
          text: 'Go to dashboard'
        }}
      />
    );
  }, 'Page not Found');

  return (
    <BrowserRouter basename={basename}>
      <Header />
      <AppContainer>
        <Routes>
          <Route path={login.path} element={<Login />} />
          <Route path={`/${id}`} element={<Outlet/>}>
            <Route path={channels.path} element={<Channels />} />
            <Route path={`${channels.path}/:channelId`} element={<Channel />}>
              <Route path="preview" element={<PreviewChannel />} />
              <Route path="streams" element={<ChannelStreams />} />
              <Route path="properties" element={<ChannelProperties />} />
              <Route path="analytics" element={<ChannelAnalytics />}>
                <Route path="reports" element={<ChannelAnalyticsReports />} />
                <Route path="publishing-history" element={<ChannelPublishingHistory />} />
                <Route path="fork-history" element={<ChannelForkHistory />} />
                <Route path="concurrents" element={<ChannelConcurrents />} />
                <Route path="ingest" element={<ChannelIngest />} />
                <Route path="" element={<Redirect to="reports" />} />
                <Route path="*" element={<PageNotFound />} />
              </Route>
              <Route path="messages" element={<ChannelMessages />} />
              <Route path="edgeauth" element={<ChannelEdgeAuth />}>
                <Route path="viewing" element={<ChannelViewingPermalinks />} />
                <Route path="publishing" element={<ChannelPublishingPermalinks />} />
                <Route path="" element={<Redirect to="viewing" />} />
                <Route path="*" element={<PageNotFound />} />
              </Route>
              <Route path="publish" element={<PublishPullChannel />} />
              <Route path="diagnose" element={<Diagnose />} />
              <Route path="*" element={<PageNotFound />} />
            </Route>
            <Route path={rooms.path} element={<Rooms />} />
            <Route path={`${rooms.path}/:roomId`} element={<Room />}>
              <Route path="preview" element={<RoomPreview />} />
              <Route path="members" element={<RoomMembers />} />
              <Route path="properties" element={<RoomProperties />} />
              <Route path="messages" element={<RoomMessages />} />
              <Route path="edgeauth" element={<RoomEdgeAuth />}>
                <Route path="viewing" element={<RoomViewingPermalinks />} />
                <Route path="publishing" element={<RoomPublishingPermalinks />} />
                <Route path="" element={<Redirect to="viewing" />} />
                <Route path="*" element={<PageNotFound />} />
              </Route>
              <Route path="publish" element={<PublishPullRoom />} />
              <Route path="diagnose" element={<Diagnose />} />
              <Route path="*" element={<PageNotFound />} />
            </Route>
            <Route path={analytics.path} element={<Analytics />}>
              <Route path="summary" element={<AnalyticsSummary />} />
              <Route path="activity" element={<AnalyticsCurrentActivity />} />
              <Route path="usage" element={<AnalyticsUsageStatistics />} />
              <Route path="ttff" element={<AnalyticsTimeToFirstFrame />} />
              <Route path="reports" element={<AnalyticsReports />} />
              <Route path="" element={<Redirect to="summary" />} />
              <Route path="*" element={<PageNotFound />} />
            </Route>
            <Route path={qos.path} element={<QoS />}/>
            <Route path={dashboard.path} element={<Dashboard />} />
            <Route path={`*`} element={<PageNotFound />} />
          </Route>
          <Route path={basename} element={<Redirect to={login.path} />} />
          <Route path={`/${id}`} element={<Redirect to={login.path} />} />
          <Route path={`/`} element={<Redirect to={login.path} />} />
        </Routes>
      </AppContainer>
      <Copyright />
    </BrowserRouter>
  );
};

export default AppRouter;